import dayjs from 'dayjs'
import type { Vehicle, VehicleAdjustment } from '../types'

export const RENT_PERIOD_OPTIONS = [
  { value: 'monthly', label: '月付' },
  { value: 'quarterly', label: '季付' },
  { value: 'halfyear', label: '半年付' },
  { value: 'yearly', label: '年付' },
  { value: 'custom', label: '自定义' },
]

export const formatPeriodLabel = (period?: string, months?: number) => {
  if (period === 'custom') {
    const value = Number(months || 0)
    const safeMonths = value > 0 ? value : 1
    return `自定义(${safeMonths}个月)`
  }
  return RENT_PERIOD_OPTIONS.find(opt => opt.value === period)?.label || period || '-'
}

export type ReceiptRecord = {
  _id?: string
  id?: string
  vehicleId?: string
  plate?: string
  model?: string
  company?: string
  customerName?: string
  amount?: number
  actualAmount?: number
  verifiedAmount?: number
  dueDate?: string
  startDate?: string
  rentPeriod?: string
  status?: string
  notes?: string
  evidenceUrls?: string[]
  periodKey?: string
}

export type PreviewItem = {
  vehicle: Vehicle
  amount: number
  actualAmount?: number
  dueDate?: string
  startDate?: string
  rentPeriod?: string
  rentPeriodMonths?: number
  chargeRangeStart?: string
  chargeRangeEnd?: string
  outstanding: number
}

const paidStatuses = ['cashier_done', 'accounted', 'completed']

export const getVehicleDueDate = (vehicle: Vehicle, asOf = dayjs()) => {
  if (vehicle.reminderDate) return vehicle.reminderDate
  const startDate = vehicle.startDate ? dayjs(vehicle.startDate) : null
  if (!startDate || !startDate.isValid()) return vehicle.dueDate
  const months = getReminderMonths(vehicle)
  if (!months) return vehicle.dueDate
  const today = asOf.startOf('day')
  let due = startDate.startOf('day')
  let guard = 0
  while (due.isBefore(today, 'day') && guard < 2400) {
    due = due.add(months, 'month')
    guard += 1
  }
  if (guard >= 2400) return vehicle.dueDate || startDate.format('YYYY-MM-DD')
  const contractEnd = vehicle.dueDate ? dayjs(vehicle.dueDate) : null
  if (contractEnd && contractEnd.isValid() && contractEnd.isBefore(due, 'day')) {
    return contractEnd.format('YYYY-MM-DD')
  }
  return due.format('YYYY-MM-DD')
}

export const matchVehicle = (item: any, vehicle: Vehicle) => {
  const id = vehicle._id || vehicle.id || vehicle.plate
  const idStr = String(id || '')
  const plateStr = String(vehicle.plate || '')
  return String(item.vehicleId || '') === idStr || String(item.plate || '') === plateStr
}

export const isPaidItem = (receiptStatus: string | undefined, itemStatus: string | undefined) => {
  return paidStatuses.includes(receiptStatus || '') || paidStatuses.includes(itemStatus || '')
}

export const getAdjustmentEffect = (vehicle: Vehicle, adjustments: VehicleAdjustment[] = []) => {
  if (!adjustments.length) {
    return { outstandingDelta: 0, revenueDelta: 0 }
  }
  const relevant = adjustments
    .filter(adj => adj.type !== 'rent_addition')
    .filter(adj => matchVehicle(adj, vehicle))
  const deductions = relevant.reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
  const revenueDelta = -deductions
  const outstandingDelta = -deductions
  return { outstandingDelta, revenueDelta }
}

export const getAdjustmentRevenueDelta = (adjustments: VehicleAdjustment[] = []) =>
  adjustments
    .filter(adj => adj.type !== 'rent_addition')
    .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0) * -1

export const getMonthsByPeriod = (record: any) => {
  const period = record?.rentPeriod || 'monthly'
  const customMonths = Number(record?.rentPeriodMonths || 0)
  switch (period) {
    case 'quarterly':
      return 3
    case 'halfyear':
      return 6
    case 'yearly':
      return 12
    case 'custom':
      return customMonths > 0 ? customMonths : 1
    default:
      return 1
  }
}

const getReminderMonths = (vehicle: Vehicle) => {
  const reminderPeriod = (vehicle as any).reminderPeriod
  if (reminderPeriod) {
    return getMonthsByPeriod({
      rentPeriod: reminderPeriod,
      rentPeriodMonths: (vehicle as any).reminderPeriodMonths
    })
  }
  return getMonthsByPeriod(vehicle)
}

export const getMonthlyAmount = (record: any) => {
  const reminderAmountNumber = Number(record?.reminderAmount)
  if (Number.isFinite(reminderAmountNumber) && reminderAmountNumber > 0) {
    const reminderMonths = getMonthsByPeriod({
      rentPeriod: record?.reminderPeriod || record?.rentPeriod || 'monthly',
      rentPeriodMonths: record?.reminderPeriodMonths ?? record?.rentPeriodMonths
    })
    return reminderAmountNumber / reminderMonths
  }
  const rawRent = record?.rentAmount
  const rentNumber = Number(rawRent)
  const rent = Number.isFinite(rentNumber) ? rentNumber : parseFloat(String(rawRent || 0))
  if (!rent) return 0
  return rent / getMonthsByPeriod(record)
}

export const getActiveLeaseStart = (vehicle: Vehicle) => {
  const currentId = (vehicle as any).currentLeaseId
  const history = Array.isArray((vehicle as any).leaseHistory) ? (vehicle as any).leaseHistory : []
  const currentLease = currentId ? history.find((l: any) => l.leaseId === currentId) : null
  const vehicleStart = vehicle.startDate ? dayjs(vehicle.startDate) : null
  if (vehicleStart && vehicleStart.isValid()) {
    return vehicleStart.format('YYYY-MM-DD')
  }

  const leaseStart = currentLease?.startDate
  if (leaseStart) return leaseStart
  if (vehicle.startDate) return vehicle.startDate
  return new Date().toISOString()
}

export const isLeaseStarted = (vehicle: Vehicle, asOf = dayjs()) => {
  const start = dayjs(getActiveLeaseStart(vehicle))
  if (!start.isValid()) return true
  return !start.isAfter(asOf, 'day')
}

export const getPricingRecords = (vehicle: Vehicle) => {
  const history = Array.isArray((vehicle as any).pricingHistory) ? (vehicle as any).pricingHistory : []
  const baseStart = getActiveLeaseStart(vehicle)
  const baseRecord = {
    startDate: baseStart,
    rentAmount: vehicle.rentAmount,
    rentPeriod: vehicle.rentPeriod,
    rentPeriodMonths: (vehicle as any).rentPeriodMonths,
    reminderAmount: (vehicle as any).reminderAmount,
    reminderPeriod: (vehicle as any).reminderPeriod,
    reminderPeriodMonths: (vehicle as any).reminderPeriodMonths
  }
  const source = history.length ? history : [baseRecord]
  const map = new Map<string, any>()
  source.forEach((rec: any) => {
    if (!rec || !rec.startDate) return
    const key = dayjs(rec.startDate).format('YYYY-MM-DD')
    const rentNumber = Number(rec.rentAmount)
    const rentAmount = Number.isFinite(rentNumber)
      ? rentNumber
      : parseFloat(String(rec.rentAmount || vehicle.rentAmount || 0))
    map.set(key, {
      startDate: key,
      rentAmount,
      rentPeriod: rec.rentPeriod || vehicle.rentPeriod,
      rentPeriodMonths: rec.rentPeriodMonths,
      reminderAmount: rec.reminderAmount ?? (vehicle as any).reminderAmount,
      reminderPeriod: rec.reminderPeriod || (vehicle as any).reminderPeriod,
      reminderPeriodMonths: rec.reminderPeriodMonths ?? (vehicle as any).reminderPeriodMonths
    })
  })
  return Array.from(map.values()).sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf())
}
export const getExpectedTillNow = (vehicle: Vehicle) => {
  const records = getPricingRecords(vehicle)
  if (vehicle?.plate) {
    console.log('[Outstanding] pricing records', {
      plate: vehicle.plate,
      records
    })
  }
  if (!records.length) return 0
  const today = dayjs()
  const dueValue = getVehicleDueDate(vehicle, today)
  const due = dueValue ? dayjs(dueValue) : null
  const endDate = due && due.isBefore(today, 'day') ? due : today
  const leaseStart = dayjs(getActiveLeaseStart(vehicle))
  const effectiveStart = leaseStart
  let sum = 0

  records.forEach((rec, idx) => {
    const segStart = dayjs(rec.startDate)
    if (!segStart.isValid()) return
    const nextStart = idx < records.length - 1 ? dayjs(records[idx + 1].startDate) : null
    const segEndRaw = nextStart && nextStart.isValid() ? nextStart.subtract(1, 'day') : endDate
    const segEnd = segEndRaw.isValid() ? segEndRaw : endDate
    if (segEnd.isBefore(segStart, 'day')) return
    const rangeStart = segStart.isAfter(effectiveStart) ? segStart : effectiveStart
    const rangeEnd = segEnd.isBefore(endDate) ? segEnd : endDate
    if (rangeEnd.isBefore(rangeStart, 'day')) return
    let cursor = rangeStart.clone()
    while (cursor.isBefore(rangeEnd) || cursor.isSame(rangeEnd, 'day')) {
      const monthStart = cursor.startOf('month')
      const monthEnd = monthStart.endOf('month')
      const endThisSlice = monthEnd.isBefore(rangeEnd) ? monthEnd : rangeEnd
      const daysInMonth = monthStart.daysInMonth()
      const days = endThisSlice.diff(cursor, 'day') + 1
      const monthly = getMonthlyAmount(rec)
      const daily = monthly / daysInMonth
      sum += daily * days
      cursor = endThisSlice.add(1, 'day')
    }
  })
  return sum
}

export const getExpectedBetween = (vehicle: Vehicle, startDate: string, endDate: string) => {
  const records = getPricingRecords(vehicle)
  if (!records.length) return 0
  const rangeStartInput = dayjs(startDate)
  const rangeEndInput = dayjs(endDate)
  if (!rangeStartInput.isValid() || !rangeEndInput.isValid()) return 0
  let rangeStart = rangeStartInput
  let rangeEnd = rangeEndInput
  if (rangeEnd.isBefore(rangeStart, 'day')) return 0

  const leaseStart = dayjs(getActiveLeaseStart(vehicle))
  if (rangeStart.isBefore(leaseStart, 'day')) {
    rangeStart = leaseStart
  }

  const dueValue = getVehicleDueDate(vehicle, rangeEnd)
  const due = dueValue ? dayjs(dueValue) : null
  if (due && rangeEnd.isAfter(due, 'day')) {
    rangeEnd = due
  }

  if (rangeEnd.isBefore(rangeStart, 'day')) return 0

  let sum = 0
  records.forEach((rec, idx) => {
    const segStart = dayjs(rec.startDate)
    if (!segStart.isValid()) return
    const nextStart = idx < records.length - 1 ? dayjs(records[idx + 1].startDate) : null
    const segEndRaw = nextStart && nextStart.isValid() ? nextStart.subtract(1, 'day') : rangeEnd
    const segEnd = segEndRaw.isValid() ? segEndRaw : rangeEnd
    if (segEnd.isBefore(segStart, 'day')) return
    const sliceStart = segStart.isAfter(rangeStart, 'day') ? segStart : rangeStart
    const sliceEnd = segEnd.isBefore(rangeEnd, 'day') ? segEnd : rangeEnd
    if (sliceEnd.isBefore(sliceStart, 'day')) return

    let cursor = sliceStart.clone()
    while (cursor.isBefore(sliceEnd, 'day') || cursor.isSame(sliceEnd, 'day')) {
      const monthStart = cursor.startOf('month')
      const monthEnd = monthStart.endOf('month')
      const endThisSlice = monthEnd.isBefore(sliceEnd, 'day') ? monthEnd : sliceEnd
      const daysInMonth = monthStart.daysInMonth()
      const days = endThisSlice.diff(cursor, 'day') + 1
      const monthly = getMonthlyAmount(rec)
      const daily = monthly / daysInMonth
      sum += daily * days
      cursor = endThisSlice.add(1, 'day')
    }
  })

  return sum
}

const getEffectiveStartDate = (vehicle: Vehicle) => {
  const records = getPricingRecords(vehicle)
  if (!records.length) return dayjs(getActiveLeaseStart(vehicle))
  const leaseStart = dayjs(getActiveLeaseStart(vehicle))
  return leaseStart
}

export const getAggregatesForVehicle = (vehicle: Vehicle, allReceipts: any[]) => {
  const extractItems = (r: any) => {
    const raw = (r as any).items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
      }
    }
    return []
  }

  let paidSum = 0
  let pendingSubmittedSum = 0
  const effectiveStart = getEffectiveStartDate(vehicle)
  allReceipts.forEach(r => {
    const items = extractItems(r)
    items.forEach((it: any) => {
      if (!matchVehicle(it, vehicle)) return
      const itemStartRaw = it.chargeRangeStart || it.periodStart || it.startDate || r.startDate
      if (itemStartRaw) {
        const itemStart = dayjs(itemStartRaw)
        if (itemStart.isValid() && itemStart.isBefore(effectiveStart, 'day')) return
      }
      const val = Number(it.receivedAmount ?? it.amount ?? 0) || 0
      if (isPaidItem((r as any).status, it.status)) {
        paidSum += val
      } else {
        pendingSubmittedSum += val
      }
    })
  })
  const expectedSum = getExpectedTillNow(vehicle)
  return { expectedSum, paidSum, pendingSubmittedSum }
}

export const getOutstanding = (
  vehicle: Vehicle,
  allReceipts: any[],
  adjustments: VehicleAdjustment[] = []
) => {
  const { expectedSum, paidSum, pendingSubmittedSum } = getAggregatesForVehicle(vehicle, allReceipts)
  const { outstandingDelta } = getAdjustmentEffect(vehicle, adjustments)
  return Math.max(expectedSum - paidSum - pendingSubmittedSum + outstandingDelta, 0)
}

export const buildPeriods = (vehicle: Vehicle) => {
  const start = dayjs(getActiveLeaseStart(vehicle)).startOf('month')
  const today = dayjs()
  const dueValue = getVehicleDueDate(vehicle, today)
  const due = dueValue ? dayjs(dueValue) : null
  const end = (due && due.isBefore(today, 'day') ? due : today).endOf('month')
  const periods: { key: string; start: string; end: string; expected: number }[] = []
  let cursor = start.clone()
  while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
    const key = cursor.format('YYYY-MM')
    const monthStart = cursor.startOf('month')
    const monthEnd = monthStart.endOf('month')
    const activePricing = getPricingRecords(vehicle)
      .filter(r => {
        const start = dayjs(r.startDate)
        return start.isBefore(monthStart, 'day') || start.isSame(monthStart, 'day')
      })
      .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf())
      .pop()
    const monthly = getMonthlyAmount(activePricing)
    periods.push({
      key,
      start: monthStart.format('YYYY-MM-DD'),
      end: monthEnd.format('YYYY-MM-DD'),
      expected: monthly
    })
    cursor = cursor.add(1, 'month')
  }
  return periods
}

export const getPaidMapByPeriod = (vehicle: Vehicle, allReceipts: any[]) => {
  const extractItems = (r: any) => {
    const raw = (r as any).items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
      }
    }
    return []
  }

  const map: Record<string, number> = {}
  const activeStart = dayjs(getActiveLeaseStart(vehicle))
  const currentLeaseId = (vehicle as any).currentLeaseId
  allReceipts.forEach(r => {
    const items = extractItems(r)
    items.forEach((it: any) => {
      if (!matchVehicle(it, vehicle)) return
      if (!isPaidItem((r as any).status, it.status)) return
      if (currentLeaseId && it.leaseId && it.leaseId !== currentLeaseId) return
      if (!it.leaseId && it.startDate && dayjs(it.startDate).isBefore(activeStart, 'day')) return
      const key = it.periodKey || dayjs(it.dueDate || it.startDate || new Date()).format('YYYY-MM')
      const val = Number(it.receivedAmount ?? it.amount ?? 0) || 0
      map[key] = (map[key] || 0) + val
    })
  })
  return map
}

export const getPeriodDeficits = (vehicle: Vehicle, allReceipts: any[]) => {
  const periods = buildPeriods(vehicle)
  const paidMap = getPaidMapByPeriod(vehicle, allReceipts)
  return periods
    .map(p => {
      const deficit = Math.max(Number(p.expected || 0) - Number(paidMap[p.key] || 0), 0)
      return deficit > 0 ? { ...p, deficit } : null
    })
    .filter(Boolean) as { key: string; start: string; end: string; expected: number; deficit: number }[]
}

export const allocatePaymentByPeriod = (
  vehicle: Vehicle,
  payAmount: number,
  allReceipts: any[],
  customMonths?: number
) => {
  if (payAmount <= 0) {
    return [{
      vehicleId: vehicle._id || vehicle.id,
      leaseId: (vehicle as any).currentLeaseId,
      plate: vehicle.plate,
      expectedAmount: payAmount,
      receivedAmount: payAmount,
      periodKey: dayjs().format('YYYY-MM'),
      dueDate: getVehicleDueDate(vehicle, dayjs()) || dayjs().endOf('month').format('YYYY-MM-DD'),
      startDate: vehicle.startDate || '',
      rentPeriod: vehicle.rentPeriod || 'monthly',
      status: 'submitted'
    }]
  }

  const periods = buildPeriods(vehicle)
  const paidMap = getPaidMapByPeriod(vehicle, allReceipts)
  let remaining = payAmount
  const items: any[] = []

  periods.forEach(p => {
    if (remaining <= 0) return
    const already = paidMap[p.key] || 0
    const deficit = Math.max(p.expected - already, 0)
    if (deficit <= 0) return
    const alloc = Math.min(deficit, remaining)
    items.push({
      vehicleId: vehicle._id || vehicle.id,
      leaseId: (vehicle as any).currentLeaseId,
      plate: vehicle.plate,
      expectedAmount: alloc,
      receivedAmount: alloc,
      periodKey: p.key,
      periodStart: p.start,
      periodEnd: p.end,
      dueDate: p.end,
      startDate: p.start,
      rentPeriod: vehicle.rentPeriod || 'monthly',
      status: 'submitted'
    })
    remaining -= alloc
  })

  if (remaining > 0) {
    const months = customMonths && customMonths > 0 ? customMonths : 1
    const periodStart = dayjs().startOf('month')
    const periodEnd = periodStart.add(months, 'month').endOf('month')
    items.push({
      vehicleId: vehicle._id || vehicle.id,
      leaseId: (vehicle as any).currentLeaseId,
      plate: vehicle.plate,
      expectedAmount: remaining,
      receivedAmount: remaining,
      periodKey: 'advance',
      periodStart: periodStart.format('YYYY-MM-DD'),
      periodEnd: periodEnd.format('YYYY-MM-DD'),
      dueDate: getVehicleDueDate(vehicle, dayjs()) || periodEnd.format('YYYY-MM-DD'),
      startDate: vehicle.startDate || '',
      rentPeriod: vehicle.rentPeriod || 'monthly',
      status: 'submitted'
    })
  }

  return items
}
